import React from 'react';
import 'react-phone-input-2/lib/style.css';
import { AmountInput, Input, Select } from '@avila-tek/ui/src';
import { banksInfo, dniTypes, searchBankNameByCode } from '../../../../static';
import {
  formatDniNumber,
  formatPhoneNumber,
  PAGO_MOVIL_ACTIONS,
} from './reducer';
import { PaymentMethodData } from '../../../../models';
import PaymentMethodFooter from './PaymentMethodFooter';
import DniInput from '../../Input/DniInput';
import Button from '../../Button/Button';
import { usePaymentProcess } from '../../../../hooks/usePaymentProcess';
import { usePagoMovilPayment } from '../../../../hooks/usePagoMovilPayment';

interface PagoMovilFormProps {
  paymentData: PaymentMethodData;
  setFormInfo: React.Dispatch<
    React.SetStateAction<{ type: string; dispatch: PaymentMethodData }>
  >;
  amount: number;
  setPhase: React.Dispatch<React.SetStateAction<number>>;
  createOrder: (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    paymentIds: Array<string>
  ) => Promise<void>;
}

function PagoMovilForm({
  paymentData,
  setFormInfo,
  amount,
  setPhase,
  createOrder,
}: PagoMovilFormProps) {
  const { refetchGtShopCartPaymentStatus } = usePaymentProcess();
  const {
    // State
    pagoMovilStateData,
    // HandlersInput
    handleSelectChange,
    handleChange,
    handleAmountChange,
    handlePhoneChange,

    // Handlers
    handleSetPhase,
    makePayment,
    loading,
  } = usePagoMovilPayment({
    paymentData,
    setFormInfo,
    amount,
    setPhase,
    createOrder,
  });

  React.useEffect(() => {
    refetchGtShopCartPaymentStatus();
  }, []);

  return (
    <section className="min-w-[90%] min-h-[33vh] relative px-6 py-3 border rounded-lg space-y-5">
      <div className="flex flex-row justify-between items-center">
        <div className="w-full">
          <h1 className="text-xl text-text-gray font-semibold">Pago movil</h1>
        </div>
        <div className="w-[30%]">
          <Button
            className="mt-5 relative z-20 bg-white border border-color-primary-300 text-color-primary-text-300 text-sm sm:text-base"
            onClick={(e) => {
              handleSetPhase(e);
            }}
          >
            Volver
          </Button>
        </div>
      </div>
      <div className="flex border rounded-lg px-6 py-3 space-y-2 w-fit">
        <h1 className="underline">Datos de pago:</h1>
        <div className="flex flex-col justify-center items-start pl-5">
          <span className=" text-sm font-arial font-extrabold mb-2 tracking-wider  ">
            Teléfono:
            <p className="text-gray-300 ">
              0{paymentData.phone.substring(2, 5)}-
              {paymentData.phone.substring(5, 8)}.
              {paymentData.phone.substring(8, 10)}.
              {paymentData.phone.substring(10)}
            </p>
          </span>
          <span className=" text-sm font-arial font-extrabold mb-2 tracking-wider  ">
            Identificador:
            <p className="text-gray-300 ">
              {paymentData.dniType}. {paymentData.dni}
            </p>
          </span>
          <span className=" text-sm font-arial font-extrabold mb-2 tracking-wider  ">
            Banco:
            <p className="text-gray-300 ">
              ({paymentData.bank}) {searchBankNameByCode(paymentData.bank)}
            </p>
          </span>
        </div>
      </div>
      <div className="w-full flex md:flex-row flex-col gap-5">
        <div className="w-full">
          <Input
            name={PAGO_MOVIL_ACTIONS.PHONE}
            value={formatPhoneNumber(pagoMovilStateData.phone)}
            label="Teléfono"
            type="text"
            placeholder="Ingrese el N° de teléfono"
            onChange={handlePhoneChange}
            required
            maxLength={11}
          />
        </div>
        <div className="w-full">
          {/* TODO falta verificar como enviar el length permitido */}
          <DniInput
            label="Cédula de identidad"
            defaultSelect={dniTypes[0].value}
            selectName={PAGO_MOVIL_ACTIONS.DNITYPE}
            inputName={PAGO_MOVIL_ACTIONS.DNI}
            selectValue={pagoMovilStateData.dnitype ?? ''}
            inputValue={formatDniNumber(pagoMovilStateData.dni) ?? ''}
            handleChangeSelect={handleSelectChange}
            handleChange={handleChange}
            placeholder="Ingrese una cédula de identidad"
          >
            {dniTypes.map((option) => (
              <option value={option.name} key={option.name}>
                {option.name}
              </option>
            ))}
          </DniInput>
        </div>
      </div>
      <div className="w-full flex md:flex-row flex-col gap-5">
        <div className="w-full">
          <Input
            name={PAGO_MOVIL_ACTIONS.REFERENCE}
            value={pagoMovilStateData.reference}
            label="N° de referencia (min-max 6 dígitos)"
            type="text"
            placeholder="Ingrese el N° de referencia"
            onChange={handleChange}
            maxLength={6}
            minLength={6}
            required
          />
        </div>
        <div className="w-full">
          <Input
            name={PAGO_MOVIL_ACTIONS.DATE}
            value={pagoMovilStateData.date}
            label="Fecha"
            type="date"
            placeholder="Ingrese una fecha"
            onChange={handleChange}
            required
          />
        </div>
      </div>
      <div className="w-full flex md:flex-row flex-col gap-5">
        <div className="w-full">
          <AmountInput
            onChange={(value: string) =>
              handleAmountChange({
                target: { value },
              } as React.ChangeEvent<HTMLInputElement>)
            }
          />
        </div>
        <div className="w-full">
          <Select
            value={pagoMovilStateData.bank}
            label="Banco"
            onChange={handleSelectChange}
            name={PAGO_MOVIL_ACTIONS.BANK}
            required
          >
            <option value="" disabled>
              Seleccione un banco
            </option>
            {banksInfo.map((option) => (
              <option value={option.code} key={option.code}>
                {option.code} - {option.name}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="w-full flex flex-row justify-center items-center mt-5">
        <Button
          className="w-1/2 bg-color-primary-300 hover:bg-color-primary-100 border-solid border border-cold-500 text-system-100 justify-center px-3 font-normal"
          onClick={(e) => makePayment(e)}
          disabled={loading}
        >
          {loading ? 'Procesando...' : 'Procesar pago'}
        </Button>
      </div>
      <PaymentMethodFooter />
    </section>
  );
}

export default PagoMovilForm;
